import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cube } from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1774780855191-3ab5f0b06932?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxyZXNpZGVudGlhbCUyMGhvdXNlJTIwdW5kZXIlMjBjb25zdHJ1Y3Rpb24lMjBmcmFtaW5nfGVufDB8fHx8MTc4MzEyOTc1Nnww&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(email, password, name);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-black">
        <img src={HERO} alt="House under construction" className="w-full h-full object-cover grayscale opacity-70" />
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <div className="flex items-center gap-2 text-white mb-4">
            <div className="bg-white rounded-lg px-3 py-2 inline-flex">
              <img src="/jnia-logo.png" alt="JNIA-BuildTrack" className="h-16 w-auto object-contain" />
            </div>
          </div>
          <p className="text-white/80 text-lg max-w-md font-heading">
            Detailed cost tracking for residential & commercial builds.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up" data-testid="register-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/jnia-logo.png" alt="JNIA-BuildTrack" className="h-12 w-auto object-contain" />
          </div>
          <p className="label-caps mb-2">Get started</p>
          <h1 className="font-heading text-4xl font-black tracking-tighter mb-8">Create account</h1>

          {error && (
            <div data-testid="register-error" className="mb-4 border border-destructive px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="label-caps">Full name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)}
                required data-testid="register-name-input" className="mt-2" placeholder="Jane Builder" />
            </div>
            <div>
              <Label htmlFor="email" className="label-caps">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="register-email-input" className="mt-2" placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="password" className="label-caps">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required minLength={6} data-testid="register-password-input" className="mt-2" placeholder="Min 6 characters" />
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="register-submit-button"
            className="w-full mt-6 rounded-none h-11">
            {loading ? "Creating…" : "Create account"}
          </Button>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" data-testid="go-login-link" className="text-primary font-semibold underline underline-offset-4">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
